import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { postApi } from '../../services/api';
import './Auth.css';

// ─── Step constants ───────────────────────────────────────────────
const STEP_EMAIL = 'email';
const STEP_OTP = 'otp';
const STEP_FORM = 'form';

const Register = () => {
  const navigate = useNavigate();

  // ── Current wizard step ────────────────────────────────────────
  const [step, setStep] = useState(STEP_EMAIL);

  // ── Email step ─────────────────────────────────────────────────
  const [emailInput, setEmailInput] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSentMsg, setOtpSentMsg] = useState('');

  // ── OTP step ───────────────────────────────────────────────────
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpVerifyError, setOtpVerifyError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRefs = useRef([]);
  const cooldownRef = useRef(null);

  // ── Register form ──────────────────────────────────────────────
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
    position: 'left',
    terms: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Success modal ──────────────────────────────────────────────
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [userCode, setUserCode] = useState('');

  // ── Cleanup cooldown timer on unmount ──────────────────────────
  useEffect(() => () => clearInterval(cooldownRef.current), []);

  // ─────────────────────────────────────────────────────────────────
  // STEP 1 – Request OTP
  // ─────────────────────────────────────────────────────────────────
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setOtpError('');
    setOtpSentMsg('');

    if (!emailInput.trim()) {
      setOtpError('Please enter a valid email address.');
      return;
    }

    setOtpLoading(true);
    try {
      const form = new FormData();
      form.append('email', emailInput.trim());

      const response = await postApi('verify-email', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.status) {
        // Store OTP in localStorage for client-side verification
        localStorage.setItem('reg_otp', String(response.data.otp));
        localStorage.setItem('reg_otp_email', emailInput.trim());

        setOtpSentMsg(response.data.message || 'OTP sent successfully to your email.');
        setFormData(prev => ({ ...prev, email: emailInput.trim() }));
        setStep(STEP_OTP);
        startCooldown();
      } else {
        setOtpError(response.data?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setOtpError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to send OTP. Please try again.'
      );
    } finally {
      setOtpLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // STEP 2 – OTP input helpers
  // ─────────────────────────────────────────────────────────────────
  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // digits only
    const updated = [...otpDigits];
    updated[index] = value;
    setOtpDigits(updated);
    setOtpVerifyError('');
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const updated = [...otpDigits];
    for (let i = 0; i < 6; i++) updated[i] = pasted[i] || '';
    setOtpDigits(updated);
    otpInputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerifyOtp = (e) => {
    e.preventDefault();
    const enteredOtp = otpDigits.join('');

    if (enteredOtp.length < 6) {
      setOtpVerifyError('Please enter the complete 6-digit OTP.');
      return;
    }

    const storedOtp = localStorage.getItem('reg_otp');
    if (enteredOtp === storedOtp) {
      localStorage.removeItem('reg_otp');
      localStorage.removeItem('reg_otp_email');
      setStep(STEP_FORM);
    } else {
      setOtpVerifyError('Invalid OTP. Please check and try again.');
      setOtpDigits(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    }
  };

  // ── Resend OTP with 60-second cooldown ────────────────────────
  const startCooldown = () => {
    setResendCooldown(60);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOtp = () => {
    if (resendCooldown > 0) return;
    setOtpDigits(['', '', '', '', '', '']);
    setOtpVerifyError('');
    setOtpSentMsg('');
    setStep(STEP_EMAIL);
  };

  // ─────────────────────────────────────────────────────────────────
  // STEP 3 – Register form handlers
  // ─────────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'phone') {
      const onlyDigits = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: onlyDigits }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match!');
      return;
    }
    if (!formData.terms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }
    if (formData.phone.length !== 10) {
      setError('Mobile number must be exactly 10 digits');
      return;
    }

    setLoading(true);
    try {
      const form = new FormData();
      form.append('email', formData.email);
      form.append('mobile', formData.phone);
      form.append('password', formData.password);
      form.append('position', formData.position);
      if (formData.referralCode?.trim()) {
        form.append('referral_code', formData.referralCode);
      }

      const response = await postApi('register', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.status) {
        const message = response.data.message || 'Registration successful!';
        const myCode = response.data.USER?.my_code || '';
        setSuccessMessage(message);
        setUserCode(myCode);
        setShowSuccessModal(true);
        setFormData({
          email: '', phone: '', password: '', confirmPassword: '',
          referralCode: '', position: 'left', terms: false,
        });
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Registration failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    setShowSuccessModal(false);
    navigate('/login');
  };

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="auth-wrapper">
      <div className="auth-bg">
        <div className="bg-animation"></div>
        <div className="floating-shapes">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="shape"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${20 + Math.random() * 10}s`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="container">
        <div className="row min-vh-100 align-items-center justify-content-center py-5">
          <div className="col-lg-6 col-md-8 col-11">
            <div className="auth-card glass-card">

              {/* ── Logo / Header ─────────────────────────── */}
              <div className="auth-header">
                <Link to="/" className="auth-logo">
                  <h2>ANTIPSUN</h2>
                  <span className="logo-tag">CRYPTO</span>
                </Link>

                {step === STEP_EMAIL && (
                  <>
                    <h3 className="auth-title">Verify Your Email</h3>
                    <p className="auth-subtitle">We'll send a one-time password to your inbox</p>
                  </>
                )}
                {step === STEP_OTP && (
                  <>
                    <h3 className="auth-title">Enter OTP</h3>
                    <p className="auth-subtitle">
                      Code sent to <span style={{ color: 'var(--accent-color)' }}>{emailInput}</span>
                    </p>
                  </>
                )}
                {step === STEP_FORM && (
                  <>
                    <h3 className="auth-title">Create Account</h3>
                    <p className="auth-subtitle">Join the future of blockchain trading</p>
                  </>
                )}

                {/* Step progress indicator */}
                <div className="otp-step-indicator">
                  <div className={`otp-step-dot ${step === STEP_EMAIL || step === STEP_OTP || step === STEP_FORM ? 'active' : ''}`} />
                  <div className={`otp-step-line ${step === STEP_OTP || step === STEP_FORM ? 'active' : ''}`} />
                  <div className={`otp-step-dot ${step === STEP_OTP || step === STEP_FORM ? 'active' : ''}`} />
                  <div className={`otp-step-line ${step === STEP_FORM ? 'active' : ''}`} />
                  <div className={`otp-step-dot ${step === STEP_FORM ? 'active' : ''}`} />
                </div>
              </div>

              {/* ════════════════════════════════════════════
                  STEP 1 – Email entry
              ════════════════════════════════════════════ */}
              {step === STEP_EMAIL && (
                <form onSubmit={handleRequestOtp} className="auth-form">
                  {otpError && (
                    <div className="alert alert-danger text-center mb-3">{otpError}</div>
                  )}

                  <div className="form-group mb-4">
                    <label className="form-label">
                      <i className="bi bi-envelope"></i> Email Address
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={emailInput}
                      onChange={e => setEmailInput(e.target.value)}
                      placeholder="Enter your email address"
                      required
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-gradient w-100 mb-3"
                    disabled={otpLoading}
                  >
                    {otpLoading
                      ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Sending OTP…</>
                      : <><i className="bi bi-send me-2"></i>Request OTP</>
                    }
                  </button>

                  <div className="auth-footer">
                    <p>Already have an account? <Link to="/login" className="auth-link">Sign In</Link></p>
                  </div>
                </form>
              )}

              {/* ════════════════════════════════════════════
                  STEP 2 – OTP verification
              ════════════════════════════════════════════ */}
              {step === STEP_OTP && (
                <form onSubmit={handleVerifyOtp} className="auth-form">
                  {otpSentMsg && (
                    <div className="alert otp-sent-alert text-center mb-3">
                      <i className="bi bi-check-circle me-2"></i>{otpSentMsg}
                    </div>
                  )}
                  {otpVerifyError && (
                    <div className="alert alert-danger text-center mb-3">{otpVerifyError}</div>
                  )}

                  {/* 6-digit OTP boxes */}
                  <div className="otp-box-wrapper mb-4">
                    {otpDigits.map((digit, i) => (
                      <input
                        key={i}
                        ref={el => (otpInputRefs.current[i] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        className="otp-box"
                        value={digit}
                        onChange={e => handleOtpChange(i, e.target.value)}
                        onKeyDown={e => handleOtpKeyDown(i, e)}
                        onPaste={i === 0 ? handleOtpPaste : undefined}
                        autoFocus={i === 0}
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-gradient w-100 mb-3"
                  >
                    <i className="bi bi-shield-lock me-2"></i>Verify OTP
                  </button>

                  <div className="otp-footer-links">
                    <button
                      type="button"
                      className="btn-text-link"
                      onClick={handleResendOtp}
                      disabled={resendCooldown > 0}
                    >
                      {resendCooldown > 0
                        ? `Resend OTP in ${resendCooldown}s`
                        : <><i className="bi bi-arrow-clockwise me-1"></i>Resend OTP</>
                      }
                    </button>
                    <button
                      type="button"
                      className="btn-text-link"
                      onClick={() => {
                        setStep(STEP_EMAIL);
                        setOtpDigits(['', '', '', '', '', '']);
                        setOtpVerifyError('');
                      }}
                    >
                      <i className="bi bi-pencil me-1"></i>Change Email
                    </button>
                  </div>
                </form>
              )}

              {/* ════════════════════════════════════════════
                  STEP 3 – Full register form
              ════════════════════════════════════════════ */}
              {step === STEP_FORM && (
                <form onSubmit={handleSubmit} className="auth-form">
                  {error && <div className="alert alert-danger text-center">{error}</div>}

                  {/* Email — pre-filled & readonly */}
                  <div className="form-group mb-3">
                    <label className="form-label">
                      <i className="bi bi-envelope"></i> Email Address
                    </label>
                    <div className="verified-email-field">
                      <input
                        type="email"
                        className="form-control"
                        value={formData.email}
                        readOnly
                      />
                      <span className="verified-badge">
                        <i className="bi bi-check-circle-fill"></i> Verified
                      </span>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="form-group mb-3">
                    <label className="form-label">
                      <i className="bi bi-phone"></i> Phone Number
                    </label>
                    <input
                      type="tel"
                      className="form-control"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter 10 digit number"
                      maxLength={10}
                      required
                    />
                  </div>

                  {/* Password */}
                  <div className="form-group mb-3 position-relative">
                    <label className="form-label">
                      <i className="bi bi-lock"></i> Password
                    </label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a strong password"
                      required
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y me-3"
                      style={{ marginTop: '8px', cursor: 'pointer' }}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </span>
                  </div>

                  {/* Confirm Password */}
                  <div className="form-group mb-3 position-relative">
                    <label className="form-label">
                      <i className="bi bi-lock-fill"></i> Confirm Password
                    </label>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="form-control"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your password"
                      required
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y me-3"
                      style={{ marginTop: '8px', cursor: 'pointer' }}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </span>
                  </div>

                  {/* Referral Code */}
                  <div className="form-group mb-3">
                    <label className="form-label">
                      <i className="bi bi-gift"></i> Referral Code (Optional)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="referralCode"
                      value={formData.referralCode}
                      onChange={handleChange}
                      placeholder="Enter referral code"
                      required
                    />
                  </div>

                  {/* Placement Position */}
                  <div className="form-group mb-3">
                    <label className="form-label">
                      <i className="bi bi-diagram-3"></i> Placement Position
                    </label>
                    <div className="d-flex gap-4">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="position"
                          value="left"
                          checked={formData.position === 'left'}
                          onChange={handleChange}
                        />
                        <label className="form-check-label">Left</label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="position"
                          value="right"
                          checked={formData.position === 'right'}
                          onChange={handleChange}
                        />
                        <label className="form-check-label">Right</label>
                      </div>
                    </div>
                  </div>

                  {/* Terms */}
                  <div className="form-group mb-4">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="terms"
                        name="terms"
                        checked={formData.terms}
                        onChange={handleChange}
                        required
                      />
                      <label className="form-check-label" htmlFor="terms">
                        I agree to the <Link to="/terms" className="auth-link">Terms of Service</Link> and{' '}
                        <Link to="/privacy" className="auth-link">Privacy Policy</Link>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-gradient w-100 mb-3"
                    disabled={loading}
                  >
                    {loading ? 'Creating Account…' : 'Create Account'}
                  </button>

                  <div className="auth-footer">
                    <p>Already have an account? <Link to="/login" className="auth-link">Sign In</Link></p>
                  </div>
                </form>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* ====================== SUCCESS MODAL ====================== */}
      {showSuccessModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0 text-center">
                <h5 className="modal-title text-success">Registration Successful!</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSuccessModal(false)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3.5rem' }}></i>
                <p className="mt-3 mb-2 text-danger fw-bold">{successMessage}</p>
                {userCode && (
                  <p className="text-white">
                    Your Referral Code: <strong>{userCode}</strong>
                  </p>
                )}
              </div>
              <div className="modal-footer border-0 justify-content-center">
                <button className="btn btn-primary px-5" onClick={handleGoToLogin}>
                  Go to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
